import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { MouseEventHandler, useState } from 'react';
import { toast } from 'sonner';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { invalidateAccountAdminQuery } from '../../../../queries/Admin/Account/useAdminAccountQuery';
import { useUpdateAdminAccountMutation } from '../../../../queries/Admin/Account/useUpdateAdminAccountMutation';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../web/components-v2/Button';
import { Input } from '../../../../web/components-v2/Input';
import { EditItemPenIcon } from '../../../../web/components/svgs/icons/EditItemPenIcon';
import { XIcon } from '../../../../web/components/svgs/icons/XIcon';
import { accountCompanyNameFieldStyles as s } from './styles';

export interface IAccountCompanyNameFieldProps {
	/** The persisted account; null until the page's account load resolves. */
	account?: Api.IAccount | null;
	/** Class for the read-mode name, so it keeps the page header's existing type treatment. */
	nameClassName: string;
	/** Styles for the edit-mode input, matching the header's existing width. */
	inputStyles: StyleDeclarationValue[];
	/** Read-mode click on the name (the header links through to the Levitate company record). */
	onNameClick: MouseEventHandler<HTMLAnchorElement>;
	/**
	 * Hands the saved account back to the page, which relays it to every `AccountDetails` instance
	 * through `lastSavedAccount`. Without it the header keeps rendering its own pre-save state copy,
	 * which react-query does not own, and the new name only appears after a reload.
	 */
	onSaved?: (account: Api.IAccount) => void;
}

/**
 * The account name in the page header, renamable in place behind a pencil. Self-contained on
 * purpose: the header's own edit mode has no Save of its own, so the previous input relied on a
 * tab's save picking the name up — which silently dropped it wherever that relay was missing.
 */
export const AccountCompanyNameField = ({
	account,
	nameClassName,
	inputStyles,
	onNameClick,
	onSaved,
}: IAccountCompanyNameFieldProps) => {
	const canEdit = useAdminUserSession().hasPermission(AdminPermission.ModifyAccount);
	const [isEditing, setIsEditing] = useState(false);
	const [draft, setDraft] = useState('');
	const mutation = useUpdateAdminAccountMutation();

	if (isEditing) {
		const save = async () => {
			const nextName = draft.trim();
			if (!account || !nextName || nextName === account.companyName) {
				setIsEditing(false);
				return;
			}
			const renamed = { ...account, companyName: nextName };
			let saved: Api.IAccount | undefined;
			try {
				saved = await mutation.mutateAsync({ account: renamed });
			} catch {
				toast.error('Failed to rename the account. Please try again.');
				return;
			}
			onSaved?.(saved ?? renamed);
			invalidateAccountAdminQuery();
			toast.success(`Account renamed to ${nextName}.`);
			setIsEditing(false);
		};

		return (
			<div className={css(s.editRow)}>
				<Input
					styles={inputStyles}
					autoFocus
					value={draft}
					placeholder='Company name'
					aria-label='Company name'
					onChange={e => setDraft(e.target.value)}
					onKeyDown={e => {
						if (e.key === 'Enter') {
							save();
						}
						if (e.key === 'Escape') {
							setIsEditing(false);
						}
					}}
				/>
				<Button variant='link' styles={[s.saveLink]} onClick={save} isLoading={mutation.isPending}>
					Save
				</Button>
				<Button
					variant='ghost'
					size='icon'
					styles={[s.cancelButton]}
					aria-label='Cancel rename'
					disabled={mutation.isPending}
					onClick={() => setIsEditing(false)}
				>
					<XIcon width={10} height={10} />
				</Button>
			</div>
		);
	}

	return (
		<div className={css(s.readRow)}>
			<a href='#' className={nameClassName} onClick={onNameClick}>
				{account?.companyName || ''}
			</a>
			{canEdit && !!account && (
				<Button
					variant='ghost'
					size='icon'
					styles={[s.editButton]}
					aria-label='Rename account'
					onClick={() => {
						setDraft(account?.companyName ?? '');
						setIsEditing(true);
					}}
				>
					<EditItemPenIcon />
				</Button>
			)}
		</div>
	);
};
